/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedData').BalancedData} BalancedData */

import React, { useCallback, useEffect, useReducer, useState } from 'react'
import BalancedHeader from './BalancedHeader'
import BalancedForm from './BalancedForm'
import AlertUI from '../../generic/AlertUI'
import { apiPut } from '../../generic/Api_Functions'
import { loadWorkflowIntoBalanced } from './balancedLoad'
import { buildSuccessorsMap, getPositionRange } from '../../generic/utilities/objectStructures/balanced/balancedConstraints'
import { isExternalJobNode } from '../../generic/utilities/objectStructures/balanced/balancedColumns'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * Root component for the balanced workflow view.
 * Owns the shared {@link BalancedData} state via `useReducer` and passes it
 * down to `BalancedHeader` and `BalancedForm`.
 *
 * @param {Object}   [props]
 * @param {Object}   [props.preloadWorkflow={}]   - Workflow entity to preload on mount; `{}` = none
 * @param {boolean}  [props.startInEditMode=false] - Enter directly in edit mode after preloading
 * @param {boolean}  [props.hideHeader=false]      - Hide `BalancedHeader` (e.g. when embedded in a dialog)
 * @param {Object}   [props.saveRef={}]            - Mutable ref the parent uses to invoke `handleSave`
 * @param {Function} [props.onSaved=()=>{}]        - Called after a successful save
 */
const Balanced = ({ preloadWorkflow = {}, startInEditMode = false, hideHeader = false, saveRef = {}, onSaved = () => {} }) => {
  /**
   * Reducer for the shared {@link BalancedData} state.
   *
   * Supported actions:
   * - `SET_EDITING` `{ editing }`            - Toggle edit mode.
   * - `SET_WORKFLOW` `{ nodes, workflow, partReference }` - Load a workflow; also
   *   stores `nodes` as `nodesSnapshot` so changes can be reverted.
   * - `SET_BALANCED_TYPE` `{ balancedType }` - Set the mode ('LINE' | 'TYPE') the cascade resolved
   *   to; resets `selectedType` and clears `balancedTypeError`, since picking a group is what
   *   resolves the block.
   * - `FLAG_MISSING_BALANCED_TYPE`            - Mark the first select in error after a save was
   *   blocked for having no position group. Presentation-only: never persisted, never sent.
   * - `SET_BALANCED_OPTION` `{ selectedType }` - Pick the concrete line / position count.
   * - `UPDATE_NODE_JOB_REFERENCE` `{ nodeId, fields }` - Merge `fields` into one node's `jobReference`.
   * - `BULK_UPDATE_NODE_JOB_REFERENCES` `{ updates: [{ nodeId, fields }] }` - Merge `jobReference`
   *   fields for many nodes at once.
   * - `RESET_NODE_JOB_REFERENCES`             - Clear placement fields (jobOrder / positionInLine /
   *   preferenceDevice) on every node.
   * - `REVERT_NODES_TO_SNAPSHOT`              - Restore `nodes` from the stored snapshot.
   * - `COMMIT_SNAPSHOT`                        - Adopt the current `nodes`/config as the new
   *   snapshot (after a successful save) so a later revert restores the saved state.
   *
   * @param {BalancedData} oldData - Current state
   * @param {{ type: string, [key: string]: * }} action - Dispatched action
   * @returns {BalancedData} Next state
   */
  const onWorkflowDataDispatch = (oldData, action) => {
    switch (action.type) {
      case 'SET_EDITING': {
        return { ...oldData, editing: action.editing }
      }
      case 'SET_WORKFLOW': {
        const newData = { ...oldData }
        newData.nodes = action.nodes
        newData.nodesSnapshot = action.nodes
        newData.workflow = action.workflow
        newData.partReference = action.partReference
        newData.balancedType = action.workflow.balancedType
        newData.balancedApproved = action.workflow.balancedApproved ?? false
        // The error mark belongs to the editing session, not to the data: a workflow never
        // inherits the red left behind by the previous one.
        newData.balancedTypeError = false
        if (action.workflow.balancedType === 'LINE') {
          newData.selectedType = action.workflow.deviceGroup
        } else {
          newData.selectedType = action.workflow.positions
        }
        newData.configSnapshot = {
          balancedType: newData.balancedType,
          selectedType: newData.selectedType,
          balancedApproved: newData.balancedApproved
        }
        return newData
      }
      case 'SET_BALANCED_TYPE': {
        return { ...oldData, balancedType: action.balancedType, selectedType: null, balancedTypeError: false }
      }
      case 'FLAG_MISSING_BALANCED_TYPE': {
        return { ...oldData, balancedTypeError: true }
      }
      case 'SET_BALANCED_OPTION': {
        return { ...oldData, selectedType: action.selectedType }
      }
      case 'SET_BALANCED_APPROVED': {
        return { ...oldData, balancedApproved: action.balancedApproved }
      }
      case 'UPDATE_NODE_JOB_REFERENCE': {
        return {
          ...oldData,
          nodes: oldData.nodes.map(n =>
            n.id === action.nodeId
              ? { ...n, jobReference: { ...n.jobReference, ...action.fields } }
              : n
          )
        }
      }
      case 'BULK_UPDATE_NODE_JOB_REFERENCES': {
        const updateMap = Object.fromEntries(action.updates.map(u => [u.nodeId, u.fields]))
        return {
          ...oldData,
          nodes: oldData.nodes.map(n =>
            updateMap[n.id]
              ? { ...n, jobReference: { ...n.jobReference, ...updateMap[n.id] } }
              : n
          )
        }
      }
      case 'RESET_NODE_JOB_REFERENCES': {
        return {
          ...oldData,
          nodes: oldData.nodes.map(n => ({
            ...n,
            jobReference: { ...n.jobReference, jobOrder: null, positionInLine: null, preferenceDevice: null }
          }))
        }
      }
      case 'REVERT_NODES_TO_SNAPSHOT': {
        const cfg = oldData.configSnapshot ?? {}
        return {
          ...oldData,
          nodes: oldData.nodesSnapshot,
          balancedType: cfg.balancedType ?? oldData.balancedType,
          selectedType: cfg.selectedType ?? oldData.selectedType,
          balancedApproved: cfg.balancedApproved ?? false
        }
      }
      case 'COMMIT_SNAPSHOT': {
        return {
          ...oldData,
          nodesSnapshot: oldData.nodes,
          configSnapshot: {
            balancedType: oldData.balancedType,
            selectedType: oldData.selectedType,
            balancedApproved: oldData.balancedApproved
          }
        }
      }
      default:
        return oldData
    }
  }

  // Bumped on every revert so children (e.g. BalancedForm) can rebuild their local
  // state from the restored snapshot via a useEffect dependency.
  const [revertKey, setRevertKey] = useState(0)

  // True while a preloaded workflow's nodes are being fetched (embedded usage).
  const [loadingWorkflow, setLoadingWorkflow] = useState(false)

  // Devices of the selected line (LINE mode), loaded by BalancedFormHeader and
  // lifted here so handleSave can resolve each node's line position by device id
  // (the node's preferenceDevice from workflowJobNodes carries no positionInLine).
  const [deviceGroupDevices, setDeviceGroupDevices] = useState([])
  // Line id the loaded devices belong to, so the remap waits for the right line's
  // devices before relocating (HU-12).
  const [deviceGroupLineId, setDeviceGroupLineId] = useState(null)

  // Position group in force, as derived by the header from the line catalogue: the number of
  // positions in generic mode, the selected line's device count in line mode. `undefined` means
  // the header has not reported yet (it is not even mounted); `null` means it reported that the
  // group is not derivable — a line whose devices are still in flight.
  const [derivedGroup, setDerivedGroup] = useState(undefined)

  /**
   * Stores the devices loaded for a line together with that line's id.
   *
   * @param {import('../../generic/utilities/objectStructures/balanced/DeviceGroupDevice').DeviceGroupDevice[]} devices
   * @param {number|null} [lineId]
   */
  const handleDevicesLoaded = useCallback((devices, lineId = null) => {
    setDeviceGroupDevices(devices)
    setDeviceGroupLineId(lineId)
  }, [])

  /**
   * Records the position group the header derived, so the save guard can tell a balancing tied to
   * a line whose size is known from one whose size has not arrived yet.
   *
   * @param {number|null} group
   */
  const handleGroupResolved = useCallback((group) => {
    setDerivedGroup(group)
  }, [])

  /**
   * Reverts all unsaved node changes back to the loaded snapshot and notifies
   * children by incrementing `revertKey`.
   *
   * @returns {void}
   */
  const handleRevert = () => {
    dispatchBalancedData({ type: 'REVERT_NODES_TO_SNAPSHOT' })
    dispatchBalancedData({ type: 'SET_EDITING', editing: false })
    setRevertKey(k => k + 1)
  }

  const [balancedData, dispatchBalancedData] = useReducer(
    onWorkflowDataDispatch,
    {
      workflow: null,
      nodes: null,
      nodesSnapshot: null,
      editing: false,
      balancedType: null,
      selectedType: null,
      balancedApproved: false,
      balancedTypeError: false,
      configSnapshot: null
    }
  )

  const [alertElement, showAlert] = AlertUI()
  const { t_ } = useSmartTranslation()

  /**
   * Whether the balancing has a position group, which is what the first select shows and what
   * every save needs (the concrete line stays optional).
   *
   * Generic mode carries the group itself, as the position count. Line mode carries it in the
   * selected line, whose size only exists once that line's devices have been read: while that is
   * in flight the first select is empty and there is no group to save yet. `undefined` means no
   * header has reported at all — nothing can be told about a line's size, so the state is taken
   * at face value.
   *
   * @returns {boolean}
   */
  const hasPositionGroup = () => {
    if (balancedData.balancedType === 'TYPE') {
      const positions = parseInt(balancedData.selectedType, 10)
      return Number.isInteger(positions) && positions >= 1
    }
    if (balancedData.balancedType === 'LINE') {
      return Boolean(balancedData.selectedType?.id) && derivedGroup !== null
    }
    return false
  }

  /**
   * Persists the current column assignment to the backend.
   *
   * Validates, stopping at the first failure: (1) when `balancedApproved` is true, that every
   * JOB node has been assigned to a column, (2) still when approved, that line advancement is
   * respected, and (3) always, that a position group has been picked. A blocked save is
   * non-destructive: nothing is sent, edit mode is kept and no snapshot is committed.
   * On success it clears the editing flag; on failure it surfaces the error.
   *
   * This is also what an embedding dialog invokes through `saveRef`, so the Fabrication Order
   * path sees the very same validations.
   *
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    if (balancedData.balancedApproved) {
      // Story 146 / S66 (RN30.1): an external JOB is out of the matrix, so it is not "pending" and
      // it never blocks an approval.
      const hasUnassigned = balancedData.nodes.some(
        n => n.nodeType === 'JOB' && !isExternalJobNode(n) && n.jobReference?.jobOrder == null
      )
      if (hasUnassigned) {
        showAlert({ title: t_('Cannot approve'), message: t_('All job nodes must be assigned to a column before approving.'), severity: 'error' })
        return
      }

      // Line-advancement: every placed JOB node must sit within its allowed
      // position range (>= predecessors, <= already-placed successors).
      const jobNodes = balancedData.nodes.filter(n => n.nodeType === 'JOB')
      const nodeById = Object.fromEntries(jobNodes.map(n => [n.id, n]))
      const successorsById = buildSuccessorsMap(balancedData.nodes)
      // LINE node positions come from the assigned device's positionInLine.
      const positionOf = (node) => {
        if (balancedData.balancedType === 'LINE') {
          return node.jobReference?.preferenceDevice?.positionInLine ?? null
        }
        return node.jobReference?.positionInLine ?? null
      }
      const positionByNodeId = {}
      for (const n of jobNodes) {
        const p = positionOf(n)
        if (p != null) positionByNodeId[n.id] = p
      }
      const positionCtx = { nodeById, positionByNodeId, successorsById }
      const violating = jobNodes.find(n => {
        const p = positionByNodeId[n.id]
        if (p == null) return false
        const { min, max } = getPositionRange(n, positionCtx)
        return p < min || p > max
      })
      if (violating) {
        showAlert({ title: t_('Line advancement is violated: {{name}} is at a position that conflicts with its preceding or following operations.', { name: violating.name }), severity: 'error' })
        return
      }
    }

    // The position group is mandatory, and it is checked LAST: a balanced that is also approved
    // with unassigned jobs or a broken line advancement keeps reporting those first, exactly as
    // it did before. A workflow saved without a derivable group is blocked for any save at all —
    // that is the deliberate way the legacy data gets cleaned up, not an oversight.
    if (!hasPositionGroup()) {
      dispatchBalancedData({ type: 'FLAG_MISSING_BALANCED_TYPE' })
      showAlert({ title: t_('Select a line type before saving.'), severity: 'error' })
      return
    }

    // Story 146 / S66 (RN30.1, D30): an external JOB contributes NO entry. Sun updates the list it
    // receives one row at a time (`UPDATE job_reference SET job_order, position_in_line,
    // preference_device WHERE id = ?`) and never deletes or clears what is absent, so leaving the
    // node out writes nothing for it: the line position cleared when it was marked (S63) cannot be
    // born again here, and every internal node is written exactly as it is today.
    const jobReferences = []
    balancedData.nodes.forEach(node => {
      if (node.nodeType === 'JOB' && !isExternalJobNode(node)) {
        jobReferences.push({
          id: node.jobReference.id,
          jobOrder: node.jobReference.jobOrder,
          positionInLine: node.jobReference.positionInLine,
          device: node.jobReference.preferenceDevice
        })
      }
    })
    const param = {
      id: balancedData.workflow.id,
      deviceGroup: balancedData.balancedType === 'LINE' ? balancedData.selectedType : null,
      balancedType: balancedData.balancedType,
      positions: balancedData.balancedType === 'TYPE' ? balancedData.selectedType : null,
      balancedApproved: balancedData.balancedApproved,
      jobReferences
    }

    await apiPut('balanced/update', param).then((response) => {
      showAlert({ message: t_('Updated correctly.'), severity: 'success' })
      // Adopt the just-saved state as the new snapshot so a later revert restores
      // the saved balancing, not the originally-loaded one.
      dispatchBalancedData({ type: 'COMMIT_SNAPSHOT' })
      dispatchBalancedData({ type: 'SET_EDITING', editing: false })
      onSaved()
    }).catch((error) => {
      showAlert({ title: t_('Error updating formula'), message: error.message, severity: error.name })
    })
  }

  // Exposes the latest handleSave so an embedding parent (e.g. a dialog) can trigger
  // the save; reassigned each render to keep the reducer-state closure current.
  saveRef.current = handleSave

  // Preloads a workflow when one is provided (embedded usage); optionally enters edit mode.
  // While the nodes are being fetched, `loadingWorkflow` drives the loading indicator.
  useEffect(() => {
    if (!preloadWorkflow.id) return
    const load = async () => {
      setLoadingWorkflow(true)
      const ok = await loadWorkflowIntoBalanced(preloadWorkflow, dispatchBalancedData)
      if (ok && startInEditMode) dispatchBalancedData({ type: 'SET_EDITING', editing: true })
      setLoadingWorkflow(false)
    }
    load()
  }, [preloadWorkflow, startInEditMode])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {alertElement}
      {!hideHeader && (
        <BalancedHeader dispatchBalancedData={dispatchBalancedData} balancedData={balancedData} onRevert={handleRevert} onSave={handleSave} />
      )}
      <BalancedForm dispatchBalancedData={dispatchBalancedData} balancedData={balancedData} revertKey={revertKey} showAlert={showAlert} loading={loadingWorkflow} deviceGroupDevices={deviceGroupDevices} deviceGroupLineId={deviceGroupLineId} onDevicesLoaded={handleDevicesLoaded} onGroupResolved={handleGroupResolved} />
    </div>
  )
}

export default Balanced
